// src/components/album/voice/AlbumVoicePlayer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Play, Pause, Volume2, VolumeX, FileText, X, RotateCcw } from 'lucide-react';

export const AlbumVoicePlayer = ({ albumVoice, voiceSettings, onClose, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);
  const audioRef = useRef(null);

  const playerPosition = voiceSettings?.albumNarration?.playerPosition || 'top-right';
  
  const positionClasses = {
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'floating': 'top-1/2 right-4 -translate-y-1/2'
  };

  useEffect(() => {
    const attemptAutoPlay = async () => {
      if (autoPlay && audioRef.current && voiceSettings?.albumNarration?.autoPlay) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setAutoPlayBlocked(false);
        } catch (error) {
          // Autoplay blocked - show play button instead
          setAutoPlayBlocked(true);
          setIsPlaying(false);
        }
      }
    };

    attemptAutoPlay();
  }, [autoPlay, voiceSettings]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      const percent = (audio.currentTime / audio.duration) * 100;
      setProgress(percent);
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`fixed ${positionClasses[playerPosition]} z-50 w-80 animate-in slide-in-from-right duration-300`}>
      <div className="bg-white rounded-xl shadow-2xl border-2 border-purple-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900">Album Story</h3>
              <p className="text-xs text-gray-500">{albumVoice.speaker?.name || 'Creator'}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Player Controls */}
        <div className="p-4 space-y-3">
          {/* Show message if autoplay was blocked */}
          {autoPlayBlocked && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
              <p className="text-xs text-purple-800 text-center">
                🎵 Click play to start the album story
              </p>
            </div>
          )}
          {/* Main Play Button */}
          <button
            onClick={togglePlay}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {isPlaying ? (
              <>
                <Pause className="w-5 h-5" />
                Pause Story
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Play Album Story
              </>
            )}
          </button>

          {/* Progress Bar */}
          <div className="space-y-1">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer group">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300 group-hover:from-purple-500 group-hover:to-pink-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>{formatTime(currentTime)}</span>
              <button
                onClick={handleRestart}
                className="hover:text-purple-600 transition-colors"
                title="Restart"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
              <span>{formatTime(albumVoice.duration || 0)}</span>
            </div>
          </div>

          {/* Transcript Toggle */}
          {voiceSettings?.albumNarration?.showTranscript !== false && albumVoice.transcript && (
            <button
              onClick={() => setShowTranscript(!showTranscript)}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              {showTranscript ? 'Hide' : 'Show'} Transcript
            </button>
          )}

          {/* Transcript */}
          {showTranscript && albumVoice.transcript && (
            <div className="p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-sm text-gray-700 max-h-40 overflow-y-auto border border-purple-100">
              <p className="leading-relaxed">{albumVoice.transcript}</p>
            </div>
          )}
        </div>

        <audio 
          ref={audioRef} 
          src={albumVoice.url} 
          preload="metadata"
        />
      </div>
    </div>
  );
};